import React, { ChangeEvent, useState } from "react";
import {
  AutomaticDownloadMode,
  Installation,
  Organization,
  Video,
  VideoCataloguesDto,
} from "../common/ObjectTypes";
import { AddIcon, DeleteIcon } from "../common/ProjectIcons";
import TextInput from "../common/TextInput";
import SelectInput from "../common/SelectInput";
import { getCurrentUser } from "../../helpers/authHeader";
import { getWildXROrgId } from "../../helpers/wildXROrgIdHelper";
import Tooltip from "@mui/material/Tooltip";
import Fade from "@mui/material/Fade";

type AddEditInstallationTypes = {
  installation: Installation;
  videoCatalogues: VideoCataloguesDto[];
  videos: Video[];
  organizations: Organization[];
  automaticDownloadModes: AutomaticDownloadMode[]; // Assuming this is an array of AutomaticDownloadMode
  errors: any;
  addInstallation: boolean;
  existingInstallation: boolean;
  onChangeSingleSelect: Function;
  onChange: Function;
  onSave: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onSelectInstallation: Function;
  onCancelEdit: Function;
  onDelete: Function;
  onSelectFavorites: Function;
};

const AddEditInstallation = ({
  installation,
  videoCatalogues,
  videos,
  organizations,
  automaticDownloadModes,
  errors,
  onChange,
  onSave,
  onCancelEdit,
  onChangeSingleSelect,
  onDelete,
  existingInstallation,
  onSelectFavorites,
}: AddEditInstallationTypes) => {
  const currentUser = getCurrentUser();
  const wildXROrgId = getWildXROrgId();
  const getCatalogueById = (id: any) => {
    return videoCatalogues.find(
      (catalogue) => catalogue.videoCatalogueId === id
    );
  };
  const defaultCatalogue = getCatalogueById(installation.videoCatalogueId);

  const getOrgById = (id: any) => {
    return organizations.find((org) => org.organizationId === id);
  };
  const defaultOrg = getOrgById(installation.organizationId);

  const getVideoNameById = (id: any) => {
    return videos.find((video) => video.videoId === id);
  };
  const defaultVideo = getVideoNameById(installation.startupVideoId);

  const getAutomaticDownloadModeById = (id: any) => {
    return automaticDownloadModes.find(
      (mode) => mode.automaticDownloadModeId === id
    );
  };

  const defaultAutomaticDownloadMode = getAutomaticDownloadModeById(
    installation.automaticDownloadModeID
  );

  const defaultFavorites = installation.favorites
    ? installation.favorites
        .filter((fav) => fav !== null && fav !== undefined) // Filter out null or undefined values
        .map((fav) => {
          const videoId = typeof fav === "string" ? fav : fav.videoId; // Extract the ID safely
          return {
            value: videoId,
            label: getVideoNameById(videoId)?.name || "",
          };
        })
    : [];

  const controlsEnum = {
    Gaze: { value: 1, label: "Gaze" },
    "Hand Controllers": { value: 2, label: "Hand Controllers" },
  };

  const defaultControl = installation.controls
    ? Object.values(controlsEnum).find(
        (env) => env.label === installation.controls
      )
    : controlsEnum.Gaze;

  const tutorialModesEnum = {
    None: { value: 1, label: "None" },
    Text: { value: 2, label: "Text" },
    Video: { value: 3, label: "Video" },
    Dynamic: { value: 4, label: "Dynamic" },
  };

  const defaultTutorialMode = installation.tutorialMode
    ? Object.values(tutorialModesEnum).find(
        (env) => env.label === installation.tutorialMode
      )
    : undefined;

  const isDisabled =
    !currentUser.isSystemAdmin &&
    !currentUser.organizations
      .map((org: any) => org.organizationId)
      .includes(installation.organizationId) &&
    installation.organizationId === wildXROrgId;
  return (
    <div className="w-full my-4">
      <div className="flex flex-col items-center">
        <h1 className="font-bold text-center text-4xl mb-6">
          {existingInstallation ? "Installation Details" : "Add Installation"}
        </h1>
        <div className="bg-grayScale-300 w-2/3">
          <div className="flex flex-col py-2">
            <div className="px-6">
              {/* name */}
              <div className="pb-2">
                <TextInput
                  name={"name"}
                  label={"Name"}
                  placeholder={""}
                  value={installation.name}
                  error={errors.name}
                  disabled={isDisabled}
                  className={"bg-white-800 p-2 w-full rounded shadow"}
                  type={"text"}
                  initialFocus={true}
                  onChange={(e: any) => onChange(e)}
                />

                {currentUser.isSystemAdmin ||
                currentUser.organizations.length > 1 ? (
                  <div className="flex flex-col mb-2 ">
                    <SelectInput
                      name="organizationId"
                      label="Select Organization"
                      options={organizations.map((org: any) => ({
                        value: org.organizationId,
                        label: org.name,
                      }))}
                      onChange={(e: any) =>
                        onChangeSingleSelect("organizationId", e)
                      }
                      className="w-full "
                      defaultValue={
                        defaultOrg
                          ? {
                              value: defaultOrg.organizationId,
                              label: defaultOrg.name,
                            }
                          : undefined
                      }
                      isDisabled={isDisabled}
                      isLoading={false}
                      isClearable={true}
                      isSearchable={true}
                      isMulti={false}
                      errors={errors.organizationId}
                    />
                  </div>
                ) : (
                  <>
                    {currentUser.organizations.map((org: any) => {
                      return (
                        <div className="py-2" key={org.organizationId}>
                          <span>Organization: </span>
                          {installation.organizationId === wildXROrgId
                            ? "WildXR"
                            : org.name}
                        </div>
                      );
                    })}
                  </>
                )}
                <TextInput
                  name={"tips"}
                  label={"Tips"}
                  placeholder={""}
                  value={installation.tips}
                  error={errors.tips}
                  disabled={isDisabled}
                  className={"bg-white-800 p-2 w-full rounded shadow"}
                  type={"text"}
                  initialFocus={true}
                  rows={6}
                  maxLength={700}
                  onChange={(e: any) => onChange(e)}
                />
                <SelectInput
                  name="tutorialMode"
                  label="Select Tutorial Mode"
                  options={Object.values(tutorialModesEnum)}
                  onChange={(e: any) => onChangeSingleSelect("tutorialMode", e)}
                  className="w-full"
                  defaultValue={defaultTutorialMode}
                  isDisabled={isDisabled}
                  isLoading={false}
                  isClearable={true}
                  isSearchable={true}
                  isMulti={false}
                  errors={errors.tutorialMode}
                />
                {installation.tutorialMode === "Text" && (
                  <TextInput
                    name={"tutorialText"}
                    label={"Tutorial Text"}
                    placeholder={""}
                    value={installation.tutorialText}
                    error={errors.tutorialText}
                    disabled={isDisabled}
                    className={"bg-white-800 p-2 w-full rounded shadow"}
                    type={"text"}
                    initialFocus={true}
                    rows={6}
                    maxLength={700}
                    onChange={(e: any) => onChange(e)}
                  />
                )}
                {currentUser.isSystemAdmin && (
                  <>
                    <TextInput
                      name={"appTimerLengthSeconds"}
                      label={"App Timer Length Seconds"}
                      placeholder={""}
                      value={installation.appTimerLengthSeconds.toString()}
                      error={errors.appTimerLengthSeconds}
                      disabled={isDisabled}
                      className={"bg-white-800 p-2 w-full rounded shadow"}
                      type={"number"}
                      initialFocus={true}
                      min={0}
                      onChange={(e: any) => {
                        const val = parseInt(e.target.value, 10);
                        if (val >= 0 || isNaN(val)) {
                          onChange(e);
                        }
                      }}
                    />
                    <TextInput
                      name={"idleTimerLengthSeconds"}
                      label={"Idle Timer Length Seconds"}
                      placeholder={""}
                      value={installation.idleTimerLengthSeconds.toString()}
                      error={errors.idleTimerLengthSeconds}
                      disabled={isDisabled}
                      className={"bg-white-800 p-2 w-full rounded shadow"}
                      type={"number"}
                      min={0}
                      initialFocus={true}
                      onChange={(e: any) => {
                        const val = parseInt(e.target.value, 10);
                        if (val >= 0 || isNaN(val)) {
                          onChange(e);
                        }
                      }}
                    />
                    <TextInput
                      name={"idleTimerDelaySeconds"}
                      label={"Idle Timer Delay Seconds"}
                      placeholder={""}
                      value={installation.idleTimerDelaySeconds.toString()}
                      error={errors.idleTimerDelaySeconds}
                      disabled={isDisabled}
                      className={"bg-white-800 p-2 w-full rounded shadow"}
                      type={"number"}
                      min={0}
                      initialFocus={true}
                      onChange={(e: any) => {
                        const val = parseInt(e.target.value, 10);
                        if (val >= 0 || isNaN(val)) {
                          onChange(e);
                        }
                      }}
                    />
                  </>
                )}
                <TextInput
                  name={"globeStartLat"}
                  label={"Globe Start Latitude"}
                  placeholder={""}
                  value={installation.globeStartLat.toString()}
                  error={errors.globeStartLat}
                  disabled={isDisabled}
                  className={"bg-white-800 p-2 w-full rounded shadow"}
                  type={"number"}
                  initialFocus={true}
                  onChange={(e: any) => onChange(e)}
                />
                <TextInput
                  name={"globeStartLong"}
                  label={"Globe Start Longitude"}
                  placeholder={""}
                  value={installation.globeStartLong.toString()}
                  error={errors.globeStartLong}
                  disabled={isDisabled}
                  className={"bg-white-800 p-2 w-full mb-3 rounded shadow"}
                  type={"number"}
                  initialFocus={true}
                  onChange={(e: any) => onChange(e)}
                />
                <hr className="my-2"></hr>
                <SelectInput
                  name="videoCatalogueId"
                  label="Select Video Catalogue"
                  options={videoCatalogues.map((catalogue: any) => ({
                    value: catalogue.videoCatalogueId,
                    label: catalogue.name,
                  }))}
                  onChange={(e: any) =>
                    onChangeSingleSelect("videoCatalogueId", e)
                  }
                  className="w-full "
                  defaultValue={
                    defaultCatalogue
                      ? {
                          value: defaultCatalogue.videoCatalogueId,
                          label: defaultCatalogue.name,
                        }
                      : undefined
                  }
                  isDisabled={isDisabled}
                  isLoading={false}
                  isClearable={true}
                  isSearchable={true}
                  isMulti={false}
                  errors={errors.videoCatalogueId}
                />
                <div className="flex items-center">
                  <input
                    onChange={(e: any) => onChange(e)}
                    checked={installation.showGraphicDeath}
                    type="checkbox"
                    id="showGraphicDeath"
                    className="mr-2"
                    name="showGraphicDeath"
                    disabled={isDisabled}
                  />
                  <label htmlFor="showGraphicDeath" className="mr-2">
                    Show Graphic Death{" "}
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    onChange={(e: any) => onChange(e)}
                    checked={installation.showGraphicSex}
                    type="checkbox"
                    id="showGraphicSex"
                    className="mr-2"
                    name="showGraphicSex"
                    disabled={isDisabled}
                  />
                  <label htmlFor="showGraphicSex" className="mr-2">
                    Show Graphic Sex{" "}
                  </label>
                </div>
                {installation.videoCatalogueId && (
                  <>
                    <SelectInput
                      name="startupVideoId"
                      label="Select Startup Video"
                      options={videos.map((video: any) => ({
                        value: video.videoId,
                        label: video.name,
                      }))}
                      onChange={(e: any) =>
                        onChangeSingleSelect("startupVideoId", e)
                      }
                      className="w-full"
                      defaultValue={
                        defaultVideo
                          ? {
                              value: installation.startupVideoId,
                              label:
                                videos.find(
                                  (video: any) =>
                                    video.videoId ===
                                    installation.startupVideoId
                                )?.name || "", // Ensure label is always a string
                            }
                          : null
                      }
                      isDisabled={isDisabled}
                      isLoading={false}
                      isClearable={true}
                      isSearchable={true}
                      isMulti={false}
                      errors={errors.startupVideoId}
                    />
                    <div className="flex items-center">
                      <input
                        onChange={(e: any) => onChange(e)}
                        checked={installation.startupVideoLoop}
                        type="checkbox"
                        id="startupVideoLoop"
                        className="mr-2"
                        name="startupVideoLoop"
                        disabled={isDisabled}
                      />
                      <label htmlFor="startupVideoLoop" className="mr-2">
                        Startup Video Loop
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        onChange={(e: any) => onChange(e)}
                        checked={installation.resumeStartupVideoOnAwake}
                        type="checkbox"
                        id="resumeStartupVideoOnAwake"
                        className="mr-2"
                        name="resumeStartupVideoOnAwake"
                        disabled={isDisabled}
                      />
                      <label
                        htmlFor="resumeStartupVideoOnAwake"
                        className="mr-2"
                      >
                        Resume Startup Video On Awake{" "}
                      </label>
                    </div>
                    <div className="flex flex-col mb-2 ">
                      <SelectInput
                        name="favorites"
                        label="Select Favorites"
                        options={videos.map((video) => ({
                          value: video.videoId,
                          label: video.name,
                        }))}
                        onChange={(selectedOptions: any) => {
                          onSelectFavorites(selectedOptions);
                        }}
                        className="w-full"
                        defaultValue={defaultFavorites || []}
                        isDisabled={isDisabled}
                        isLoading={false}
                        isClearable={true}
                        isSearchable={true}
                        isMulti={true}
                        errors={errors.favorites}
                      />{" "}
                    </div>

                    <div className="flex items-center">
                      <input
                        onChange={(e: any) => onChange(e)}
                        checked={installation.filterFavoritesByDefault}
                        type="checkbox"
                        id="filterFavoritesByDefault"
                        className="mr-2"
                        name="filterFavoritesByDefault"
                        disabled={isDisabled}
                      />
                      <label
                        htmlFor="filterFavoritesByDefault"
                        className="mr-2"
                      >
                        Filter Favorites By Default{" "}
                      </label>
                    </div>
                  </>
                )}
                <hr className="my-2"></hr>

                <div className="flex flex-row">
                  <span className="text-sm flex items-center">
                    Select Automatic Download Mode
                  </span>
                  <Tooltip
                    title="Automatically downloading a large number of videos will be slow and depend on the internet speed. Downloading videos can also impact the quality of streaming videos."
                    arrow
                    slotProps={{ tooltip: { sx: { fontSize: ".75em" } } }}
                    TransitionComponent={Fade}
                    TransitionProps={{ timeout: 600 }}
                  >
                    <span className="ml-1 inline-flex items-center justify-center w-4 h-4 border border-gray-400 rounded-full text-xs text-gray-600">
                      ?
                    </span>
                  </Tooltip>
                </div>
                <SelectInput
                  name="automaticDownloadModeID"
                  label=""
                  options={automaticDownloadModes.map((mode: any) => ({
                    value: mode.automaticDownloadModeId,
                    label: mode.name,
                  }))}
                  onChange={(e: any) =>
                    onChangeSingleSelect("automaticDownloadModeID", e)
                  }
                  className="w-full"
                  defaultValue={
                    defaultAutomaticDownloadMode
                      ? {
                          value: installation.automaticDownloadModeID,
                          label:
                            automaticDownloadModes.find(
                              (mode: any) =>
                                mode.automaticDownloadModeId ===
                                installation.automaticDownloadModeID
                            )?.name || "", // Ensure label is always a string
                        }
                      : null
                  }
                  isDisabled={isDisabled}
                  isLoading={false}
                  isClearable={true}
                  isSearchable={true}
                  isMulti={false}
                  errors={errors.automaticDownloadModeID}
                />
                <hr className="my-2"></hr>
                {currentUser.isSystemAdmin && (
                  <SelectInput
                    name="controls"
                    label="Select Control"
                    options={Object.values(controlsEnum)}
                    onChange={(e: any) => onChangeSingleSelect("controls", e)}
                    className="w-full"
                    defaultValue={defaultControl}
                    isDisabled={isDisabled}
                    isLoading={false}
                    isClearable={true}
                    isSearchable={true}
                    isMulti={false}
                    errors={errors.controls}
                  />
                )}

                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="flex items-center">
                    <input
                      onChange={(e: any) => onChange(e)}
                      checked={installation.demoMode}
                      type="checkbox"
                      id="demoMode"
                      className="mr-2"
                      name="demoMode"
                      disabled={isDisabled}
                    />
                    <label htmlFor="demoMode" className="mr-2">
                      Demo Mode{" "}
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      onChange={(e: any) => onChange(e)}
                      checked={installation.showMenuTray}
                      type="checkbox"
                      id="showMenuTray"
                      className="mr-2"
                      name="showMenuTray"
                      disabled={isDisabled}
                    />
                    <label htmlFor="showMenuTray" className="mr-2">
                      Show Menu Tray{" "}
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      onChange={(e: any) => onChange(e)}
                      checked={installation.shouldSendSessionData}
                      type="checkbox"
                      id="shouldSendSessionData"
                      className="mr-2"
                      name="shouldSendSessionData"
                      disabled={isDisabled}
                    />
                    <label htmlFor="shouldSendSessionData" className="mr-2">
                      Send Session Data{" "}
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {!isDisabled && (
        <div className="py-2 flex flex-row justify-center">
          <div className="w-2/3 flex justify-end items-end">
            <button
              className="px-3 py-2 rounded bg-deepGreen-500 text-white hover:bg-deepGreen-700 drop-shadow-lg"
              onClick={() => onCancelEdit()}
            >
              Cancel
            </button>
            <button
              className="px-3 py-2 ml-2 rounded bg-deepGreen-500 text-white hover:bg-deepGreen-700 drop-shadow-lg"
              onClick={(e) => onSave(e)}
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddEditInstallation;
